/**
 * CRT Shader Passes
 * Main shader programs used for CRT effect rendering.
 * Individual effect files are included via #include directives.
 */

import bloom from "./effects/bloom.glsl?raw";
import brightness from "./effects/brightness.glsl?raw";
import burnIn from "./effects/burnIn.glsl?raw";
import BurnInAccumulationEffect from "./effects/burnInAccumulation.glsl?raw";
import chromaColor from "./effects/chromaColor.glsl?raw";
import flickering from "./effects/flickering.glsl?raw";
import glowingLine from "./effects/glowingLine.glsl?raw";
import horizontalSync from "./effects/horizontalSync.glsl?raw";
import jitter from "./effects/jitter.glsl?raw";
import noiseTexture from "./effects/noiseTexture.glsl?raw";
import powerTransition from "./effects/powerTransition.glsl?raw";
import rasterization from "./effects/rasterization.glsl?raw";
import rgbShift from "./effects/rgbShift.glsl?raw";
import staticNoise from "./effects/staticNoise.glsl?raw";
import VertexShader from "./effects/vertexShader.glsl?raw";

const DynamicPassFragmentShader = /* glsl */ `
precision mediump float;

uniform sampler2D frameBuffer;
uniform sampler2D burnInSource;
uniform sampler2D textTexture;
uniform vec3 fontColor;
uniform vec3 backgroundColor;
uniform float chromaColor;
uniform float ambientLight;
uniform vec2 resolution;

uniform float time;
uniform float flickering;
uniform float horizontalSync;
uniform float jitter;
uniform float staticNoise;
uniform float glowingLine;
uniform float burnIn;
uniform int rasterizationMode;
uniform float rasterizationIntensity;
uniform vec2 virtualResolution;
uniform float powerState;
uniform float powerDirection;
uniform float channelSwitchFlicker;

${noiseTexture}

varying vec2 vUv;

${chromaColor}
${flickering}
  ${horizontalSync}
${jitter}
${staticNoise}
${glowingLine}
${burnIn}
${rasterization}
${powerTransition}

void main() {
  vec2 cc = vec2(0.5) - vUv;
  float distance = length(cc);

  vec2 staticCoords = vUv;

  vec4 initialNoiseTexel = sampleInitialNoise(time);

  float flickeringBrightness = calculateFlickeringBrightness(initialNoiseTexel, flickering);

  float distortionScale = getHorizontalSyncDistortionScale(initialNoiseTexel, horizontalSync);

  float noise = staticNoise;

  vec2 coords = vUv;

  coords = applyHorizontalSync(coords, time, initialNoiseTexel, horizontalSync);
  if(horizontalSync > 0.0) {
    noise += distortionScale * 7.0;
  }

  vec4 screenNoiseTexel = sampleScreenNoise(coords, time);

  vec2 txt_coords = applyJitter(coords, screenNoiseTexel, jitter);

  // Clamp jittered coords to avoid sampling outside the static pass output
  txt_coords = clamp(txt_coords, 0.0, 1.0);

  float colorAccum = 0.0001;

  if(staticNoise > 0.0 || distortionScale > 0.0) {
    float noiseVal = screenNoiseTexel.a;
    colorAccum += noiseVal * noise * (1.0 - distance * 1.3);
  }

  if(glowingLine > 0.0) {
    colorAccum += calculateGlowingLineContribution(coords, virtualResolution, time, glowingLine);
  }

  vec3 txt_color = texture2D(frameBuffer, txt_coords).rgb;

  if(burnIn > 0.0) {
    vec3 burnInColor = texture2D(burnInSource, txt_coords).rgb;
    txt_color = applyBurnIn(txt_color, burnInColor, burnIn);
  }

  txt_color += fontColor * colorAccum;

  txt_color = applyRasterization(staticCoords, txt_color, virtualResolution, rasterizationIntensity, rasterizationMode);

  vec3 finalColor = txt_color;

  if(flickering > 0.0) {
    finalColor *= flickeringBrightness;
  }

  if(ambientLight > 0.0) {
    finalColor += vec3(ambientLight * 0.2) * (1.0 - distance) * (1.0 - distance);
  }

  // Apply channel switch flicker effect
  if(channelSwitchFlicker > 0.0) {
    // Sample noise for random flicker pattern
    vec4 flickerNoise = sampleScreenNoise(vUv, time * 50.0);
    
    // Create horizontal tearing/rolling effect
    float roll = sin(vUv.y * 30.0 + time * 0.1) * channelSwitchFlicker * 0.5;
    float tear = step(0.5, fract(vUv.y * 8.0 + time * 0.05)) * channelSwitchFlicker * 0.3;
    
    // Random brightness flashes
    float flash = flickerNoise.r * channelSwitchFlicker * 2.0;
    
    // Darken overall with random bright spots
    float flickerBrightness = mix(1.0, 0.1, channelSwitchFlicker) + flash;
    finalColor *= flickerBrightness;
    
    // Add horizontal sync distortion appearance
    finalColor += vec3(tear * 0.5);
    
    // Add some color fringing during flicker
    finalColor.r += roll * 0.1;
    finalColor.b -= roll * 0.1;
    
    // Add static/snow during channel switch
    float staticSnow = flickerNoise.a * channelSwitchFlicker * 0.8;
    finalColor = mix(finalColor, vec3(staticSnow), channelSwitchFlicker * 0.6);
  }

  // Apply power on/off transition effect
  finalColor = applyPowerTransition(finalColor, vUv, powerState, powerDirection, time);

  gl_FragColor = vec4(finalColor, 1.0);
}

`;

const StaticPassFragmentShader = /* glsl */ `
precision mediump float;

uniform sampler2D textTexture;
uniform vec3 fontColor;
uniform vec3 backgroundColor;
uniform float chromaColor;
uniform float rgbShift;
uniform float bloom;
uniform float brightness;
uniform vec2 resolution;

varying vec2 vUv;

${chromaColor}
${rgbShift}
${bloom}
${brightness}

void main() {
  vec2 txt_coords = vUv;

  vec3 txt_color = texture2D(textTexture, txt_coords).rgb;

  if(rgbShift > 0.0) {
    txt_color = applyRgbShift(textTexture, txt_coords, rgbShift);
  }

  txt_color += vec3(0.0001);
  float greyscale_color = rgb2grey(txt_color);

  vec3 finalColor;
  if(chromaColor > 0.0) {
    vec3 foregroundColor = mix(fontColor, txt_color * fontColor / greyscale_color, chromaColor);
    finalColor = mix(backgroundColor, foregroundColor, greyscale_color);
  } else {
    finalColor = mix(backgroundColor, fontColor, greyscale_color);
  }

  if(bloom > 0.0) {
    finalColor = applyBloom(finalColor, textTexture, txt_coords, resolution, bloom, fontColor, chromaColor);
  }

  float screen_brightness = mix(0.5, 1.5, brightness);
  finalColor *= screen_brightness;

  gl_FragColor = vec4(finalColor, 1.0);
}

`;

export {
  BurnInAccumulationEffect,
  DynamicPassFragmentShader,
  StaticPassFragmentShader,
  VertexShader,
};
